import flatten from "lodash.flatten";

import { OutputRow as GetSingleSessionPricesOutput } from "api/getSingleSessionPrices";
import {
	getCurrencyConversion,
	Output as CurrencyConversionOutput
} from "api/getCurrencyConversion";
import { SearchResults } from "types-and-validators/SearchResults";
import { MentorProduct } from "types-and-validators/MentorProduct";
import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import { Auth } from "api/makeApiRequest";

export interface OutputRow extends GetSingleSessionPricesOutput {
	approximateAmountInDefaultCurrency?: number | undefined;
}

type Params<T> = {
	reqs: ApiRequest<SearchResults<T>>[];
	doCurrencyConversion?: boolean;
	defaultCurrencyCode?: string;
	auth: Auth;
};

export function addCurrencyConversions<
	T extends Pick<MentorProduct, "currency" | "price">
>({ reqs, doCurrencyConversion, defaultCurrencyCode, auth }: Params<T>) {
	let currencyConversionRequests: ApiRequest<CurrencyConversionOutput>[] = [];

	let aborted = false;

	return {
		ready: (async () => {
			const allOutputs = (await Promise.all(
				reqs.map(async req => {
					const rows = await req.ready;
					if (!rows) {
						return rows;
					}

					return {
						...rows,
						page: await Promise.all(
							rows.page.map(async entry => {
								const subReq =
									doCurrencyConversion &&
									defaultCurrencyCode &&
									defaultCurrencyCode !== entry.currency
										? getCurrencyConversion(
												{
													sourceCurrency: entry.currency,
													sourceAmount: entry.price,
													destinationCurrency: defaultCurrencyCode
												},
												auth
										  )
										: undefined;

								if (subReq) {
									currencyConversionRequests.push(subReq);
								}

								const convertedAmount = subReq ? await subReq.ready : undefined;

								return {
									...entry,
									approximateAmountInDefaultCurrency: convertedAmount
										? convertedAmount.destinationAmount
										: undefined
								};
							})
						)
					};
				})
			)).map(response => (response ? response.page : []));

			return { page: flatten(allOutputs) };
		})(),
		abort: () => {
			for (const req of reqs) {
				req.abort();
			}
			for (const currencyConversionRequest of currencyConversionRequests) {
				currencyConversionRequest.abort();
			}
			aborted = true;
		},
		aborted: () => !!aborted
	};
}
